// src/components/InviteOperator.jsx
//
// Modal component for CommandCenter — generates a team join token
// and displays QR code + shareable link for field onboarding.

import { useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { QRCode } from 'react-qr-code'
import { X, Copy, Check, Users, Download } from 'lucide-react'
import { downloadQRPng } from '../lib/printQR'

const BASE_URL = import.meta.env.VITE_APP_URL || 'https://death-card.vercel.app'

export default function InviteOperator({ teamId, teamName, userId, onClose }) {
  const [token, setToken] = useState(null)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState(null)

  const generateToken = async () => {
    setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from('join_tokens')
      .insert({
        created_by: userId,
        join_type: 'team',
        team_id: teamId,
      })
      .select('token')
      .limit(1)
    if (err) {
      setError('Failed to generate invite: ' + err.message)
      setLoading(false)
      return
    }
    setToken(data?.[0]?.token)
    setLoading(false)
  }

  const joinUrl = token ? `${BASE_URL}/join/${token}` : ''

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback for non-HTTPS
      const input = document.createElement('input')
      input.value = joinUrl
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(0,0,0,.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #2a2e36, #1a1d22)',
        border: '1px solid #4a5058',
        boxShadow: '0 8px 32px rgba(0,0,0,.6)',
        padding: '24px',
        width: '100%',
        maxWidth: '360px',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Users size={16} style={{ color: '#ff7a18' }} />
            <span style={{
              fontFamily: "'Orbitron', sans-serif",
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: '#eaf2fb',
            }}>
              INVITE OPERATOR
            </span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5a6470', padding: '4px' }}>
            <X size={18} />
          </button>
        </div>

        {/* Team name */}
        <p style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: '11px',
          color: '#5a6470',
          letterSpacing: '0.1em',
          marginBottom: '16px',
        }}>
          TEAM: {teamName?.toUpperCase()}
        </p>

        {!token ? (
          <>
            <p style={{ fontSize: '13px', color: '#9aa6b0', marginBottom: '16px', lineHeight: '1.5' }}>
              Generate a one-time QR code for a new operator to join this team. 
              The link expires in 24 hours and can only be used once.
            </p>
            {error && (
              <p style={{ fontSize: '12px', color: '#ff4136', marginBottom: '12px' }}>{error}</p>
            )}
            <button 
              onClick={generateToken} 
              disabled={loading}
              style={{
                width: '100%',
                fontFamily: "'Orbitron', sans-serif",
                fontSize: '13px',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                background: loading ? '#4a5058' : 'linear-gradient(135deg, #ff7a18, #d8500a)',
                color: '#fff5e8',
                border: 'none',
                padding: '14px 20px',
                cursor: loading ? 'wait' : 'pointer',
                boxShadow: loading ? 'none' : '0 0 14px rgba(255,122,24,.5)',
              }}
            >
              {loading ? 'GENERATING...' : 'GENERATE INVITE'}
            </button>
          </>
        ) : (
          <>
            {/* QR Code */}
            <div style={{
              background: '#ffffff',
              padding: '16px',
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '16px',
            }}>
              <QRCode id="invite-team-qr" value={joinUrl} size={200} />
            </div>

            <button
              onClick={() => downloadQRPng({ svgElementId: 'invite-team-qr', filename: `dc-team-${(teamName || 'invite').replace(/\s+/g, '-')}` })}
              style={{
                width: '100%',
                marginBottom: '12px',
                background: 'rgba(255,122,24,.06)',
                border: '1px solid rgba(255,122,24,.3)',
                color: '#ff7a18',
                padding: '8px 12px',
                fontSize: '12px',
                fontFamily: "'IBM Plex Mono', monospace",
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              <Download size={12} /> Save QR as PNG
            </button>

            {/* Link display + copy */}
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '16px',
            }}>
              <div style={{
                flex: 1,
                background: 'rgba(77,214,255,.04)',
                border: '1px solid rgba(77,214,255,.25)',
                padding: '10px 12px',
                fontSize: '11px',
                fontFamily: "'IBM Plex Mono', monospace",
                color: '#9aa6b0',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {joinUrl}
              </div>
              <button 
                onClick={copyLink}
                style={{
                  background: copied ? 'rgba(77,214,255,.15)' : 'rgba(77,214,255,.06)',
                  border: '1px solid rgba(77,214,255,.25)',
                  color: copied ? '#4dd6ff' : '#9aa6b0',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>

            <p style={{
              fontSize: '11px',
              color: '#5a6470',
              textAlign: 'center',
              marginBottom: '16px',
            }}>
              Show this QR code to the operator or share the link.
              Single use — expires in 24 hours.
            </p>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                onClick={() => { setToken(null); setCopied(false) }}
                style={{
                  flex: 1,
                  fontFamily: "'Saira Condensed', sans-serif",
                  fontSize: '13px',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  background: 'rgba(77,214,255,.06)',
                  color: '#4dd6ff',
                  border: '1px solid rgba(77,214,255,.25)',
                  padding: '10px',
                  cursor: 'pointer',
                }}
              >
                NEW INVITE
              </button>
              <button 
                onClick={onClose}
                style={{
                  flex: 1,
                  fontFamily: "'Saira Condensed', sans-serif",
                  fontSize: '13px',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  background: 'rgba(255,255,255,.04)',
                  color: '#5a6470',
                  border: '1px solid #4a5058',
                  padding: '10px',
                  cursor: 'pointer',
                }}
              >
                DONE
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
